import React from "react";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import type { QualificationItem } from "../../../../api/qualification/Qualification.typed";
import { useCustomer, useQualification } from "../../../../contexts/api";
import { useBilling } from "../../../../contexts/api/billing";
import { usePermissions, Permission } from "../../../../contexts/app/permissions";
import { normalizeCustomerEmoji } from "../../../common/customerEmoji";
import type { ProfileFormState } from "./profileFormTypes";
import { initialProfileFormState, toDateInputValue } from "./profileFormTypes";

export type UseProfileFormParams = {
  customer: ZekayCustomer | undefined;
  setProfileFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomerUpdated: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
  editable?: boolean;
  onCustomerCreated?: (customer: ZekayCustomer) => void;
  /** When true, close the modal after a successful create (embedded flows e.g. excursion). */
  closeAfterCreate?: boolean;
};

function isDraftCustomer(c: ZekayCustomer | undefined): boolean {
  return Boolean(c) && (c!.Id === undefined || c!.Id === null);
}

export function useProfileForm({
  customer,
  setProfileFormOpen,
  setCustomerUpdated,
  editable = false,
  onCustomerCreated,
  closeAfterCreate = true,
}: UseProfileFormParams) {
  const { updateCustomer, createCustomer } = useCustomer();
  const { listQualificationItems } = useQualification();
  const { purchaseProduct } = useBilling();
  const [saving, setSaving] = React.useState(false);
  const [qualificationItems, setQualificationItems] = React.useState<QualificationItem[]>([]);
  const { hasPermission } = usePermissions();
  const canEditProfile = editable || hasPermission(Permission.CUSTOMERS_PROFILE_EDIT);

  const [form, setForm] = React.useState<ProfileFormState>(initialProfileFormState);
  const [associateProductIds, setAssociateProductIds] = React.useState<number[]>([]);
  /** Hide counters/Product tab after create succeeds until modal closes or a new draft opens (fresh mounts skip until cleared via draft effect). */
  const [hideProductsTabUntilDraftAgain, setHideProductsTabUntilDraftAgain] = React.useState(false);

  React.useEffect(() => {
    if (!customer) return;
    setForm({
      FirstName: customer.FirstName ?? "",
      LastName: customer.LastName ?? "",
      MailAddress: customer.MailAddress ?? "",
      Phonenumber: customer.Phonenumber ?? "",
      Street: customer.Street ?? "",
      PostalCode: customer.PostalCode ?? "",
      City: customer.City ?? "",
      Birthdate: toDateInputValue(customer.Birthdate),
      ReceiveNewsletter: customer.GdprConsent ?? false,
      LastQualification: customer.LastQualification ?? "",
      Emoji: normalizeCustomerEmoji(customer.Emoji ?? undefined) ?? "",
    });
  }, [customer]);

  React.useEffect(() => {
    if (isDraftCustomer(customer)) setHideProductsTabUntilDraftAgain(false);
  }, [customer]);

  React.useEffect(() => {
    if (!editable || !customer) return;
    listQualificationItems().then((items) => setQualificationItems(items ?? []));
  }, [editable, customer, listQualificationItems]);

  const handleClose = React.useCallback(() => {
    setProfileFormOpen(false);
  }, [setProfileFormOpen]);

  const handleSave = React.useCallback(async () => {
    if (!editable || !customer) return;
    setSaving(true);
    try {
      const birthdateValue = form.Birthdate ? new Date(`${form.Birthdate}T00:00:00`) : undefined;
      const emojiNorm = normalizeCustomerEmoji(form.Emoji || undefined);
      const merged: ZekayCustomer = {
        ...customer,
        FirstName: form.FirstName || undefined,
        LastName: form.LastName || undefined,
        MailAddress: form.MailAddress || undefined,
        Phonenumber: form.Phonenumber || undefined,
        Street: form.Street || undefined,
        PostalCode: form.PostalCode || undefined,
        City: form.City || undefined,
        Birthdate: (birthdateValue ?? customer.Birthdate) as Date,
        GdprConsent: form.ReceiveNewsletter,
        LastQualification: form.LastQualification || undefined,
        Emoji: emojiNorm ?? "",
      };

      if (isDraftCustomer(customer)) {
        const created = await createCustomer(merged);
        if (!created) return;
        const productIdsToPurchase = [...new Set(associateProductIds)];
        setCustomerUpdated(created);
        onCustomerCreated?.(created);
        if (created.Id != null && productIdsToPurchase.length > 0) {
          for (const productId of productIdsToPurchase) {
            await purchaseProduct({
              customer_id: created.Id,
              product_id: productId,
            });
          }
        }
        setAssociateProductIds([]);
        setHideProductsTabUntilDraftAgain(true);
        if (closeAfterCreate) {
          handleClose();
        }

        return;
      }

      const updated = await updateCustomer(customer.Id!, merged);
      if (updated) {
        setCustomerUpdated(updated);
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  }, [
    customer,
    editable,
    form,
    createCustomer,
    associateProductIds,
    purchaseProduct,
    updateCustomer,
    setCustomerUpdated,
    handleClose,
    onCustomerCreated,
    closeAfterCreate,
  ]);

  const title =
    customer == null
      ? "Profil"
      : isDraftCustomer(customer)
        ? "Nouveau client"
        : `${customer.FirstName ?? ""} ${customer.LastName ?? ""}`.trim() || "Profil";
  const showFooter = editable && !!customer;
  const saveValid = Boolean(form.FirstName?.trim() && form.LastName?.trim());
  const confirmLabel = isDraftCustomer(customer) ? "Créer" : "Enregistrer";

  return {
    form,
    setForm,
    qualificationItems,
    saving,
    canEditProfile,
    handleClose,
    handleSave,
    title,
    showFooter,
    saveValid,
    confirmLabel,
    associateProductIds,
    setAssociateProductIds,
    hideProductsTabUntilDraftAgain,
  };
}
